import { useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Counter, DatePicker, DateRangeCalendar, Popover } from '@indipia/ui-core'
import type { Dependency, GanttTask } from '../types/gantt.types'
import type { HolidayState } from '../hooks/useHolidays'
import {
  addWorkdays, computeShiftReschedule, countWorkdays, formatISO, getCascadeIds, holidayGateError, nextWorkday,
  predecessorWarnings, type CascadePlan,
} from '../views/scheduleUtils'
// 기간 수정 팝오버(A-15) — 상세창 기간 칩을 눌러 연다. 시작일·종료일·일수(작업일 수)와 2개월 달력, 후행 동반 이동,
// 발주·입고 동반 이동, 선행 경고를 한 곳에서 다루고 [적용]으로 간트의 미저장 변경에 누적한다(즉시 저장하지 않음).
// 입력은 이 팝오버 안에서만 보관하고 [취소]·바깥 클릭·Esc 는 폐기한다. 소요일은 항상 작업일 수(일요일·공휴일 제외).
// 규칙(계획 3장 판정): 비작업일 시작일은 선택 후 다음 작업일로 보정 / 후행은 달력일 간격을 보존하고 도착일이 비작업일이면
// 다음 작업일 / 표시 기간 밖으로 나가는 날짜가 있으면 [적용] 막음 / 선행 경고는 적용을 막지 않음.

interface Props {
  anchorRef: React.RefObject<HTMLElement | null>
  task: GanttTask
  tasks: GanttTask[]
  deps: Dependency[]
  holidayState: HolidayState
  /** 이동 가능 범위(표시 기간 앞뒤 3일) — 이 밖의 날짜는 고를 수 없고 계획이 이 밖으로 나가면 [적용]이 막힌다 */
  movableRange: { start: Date; end: Date }
  /** 계획을 간트에 반영. 반영했으면 true */
  onApply: (plan: CascadePlan, opts: { syncOrderDates: boolean }) => boolean
  onClose: () => void
}

const MAX_DAYS = 365

const isoOf = (d: Date) => formatISO(d)
const fromIso = (s: string) => new Date(`${s}T00:00:00`)
const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime()

export function PeriodEditPopover({ anchorRef, task, tasks, deps, holidayState, movableRange, onApply, onClose }: Props) {
  const holidays = holidayState.dates
  const holidayOk = holidayState.status === 'success'

  // 팝오버 입력 상태 — 열릴 때 공종의 현재 값으로 시작(소요일은 작업일 수). 공휴일을 쓸 수 없으면 0 으로 두고 입력을 막는다.
  const initDays = holidayOk && holidayGateError(holidayState, task.startDate, task.endDate) === null
    ? Math.max(1, countWorkdays(task.startDate, task.endDate, holidays))
    : 1
  const [start, setStart] = useState<Date>(task.startDate)
  const [end, setEnd] = useState<Date>(task.endDate)
  const [days, setDays] = useState<number>(initDays)
  const hasSuccessors = useMemo(() => getCascadeIds(task.taskId, deps).length > 1, [task.taskId, deps])
  const [withSuccessors, setWithSuccessors] = useState(true)
  const [syncOrders, setSyncOrders] = useState(true)

  const minIso = isoOf(movableRange.start)
  const maxIso = isoOf(movableRange.end)

  // 시작일 선택 — 비작업일이면 다음 작업일로 보정하고, 일수(작업일 수)를 유지해 종료일을 옮긴다
  const pickStart = (d: Date) => {
    if (!holidayOk) return
    const s = nextWorkday(d, holidays)
    setStart(s)
    setEnd(addWorkdays(s, days, holidays))
  }
  // 종료일 선택 — 시작일보다 앞서면 거부, 일수 = 시작~종료의 작업일 수
  const pickEnd = (d: Date) => {
    if (!holidayOk || d.getTime() < start.getTime()) return
    setEnd(d)
    setDays(Math.min(MAX_DAYS, Math.max(1, countWorkdays(start, d, holidays))))
  }
  const changeDays = (n: number) => {
    if (!holidayOk) return
    const v = Math.min(MAX_DAYS, Math.max(1, Math.round(n)))
    setDays(v)
    setEnd(addWorkdays(start, v, holidays))
  }

  // 현재 입력으로 만든 계획·경고·차단 사유 — 입력이 바뀔 때마다 다시 계산해 팝오버 안에서 보여준다
  const result = useMemo(() => {
    const none = { plan: null as CascadePlan | null, error: null as string | null, warnings: [] as string[], changed: false, hasOrderRow: false }
    if (!holidayOk) return none
    try {
      const involved = getCascadeIds(task.taskId, deps)
      const involvedDates: Date[] = []
      for (const t of tasks) if (involved.includes(t.taskId)) involvedDates.push(t.startDate, t.endDate)
      const preErr = holidayGateError(holidayState, start, end, ...involvedDates)
      if (preErr) return { ...none, error: preErr }

      const plan: CascadePlan = withSuccessors && hasSuccessors
        ? computeShiftReschedule(task.taskId, start, end, tasks, deps, holidays)
        : new Map([[task.taskId, { startDate: start, endDate: end }]])

      const planDates: Date[] = []
      for (const v of plan.values()) planDates.push(v.startDate, v.endDate)
      const postErr = holidayGateError(holidayState, ...planDates)
      if (postErr) return { ...none, error: postErr }

      const changedPlan: CascadePlan = new Map()
      for (const [id, v] of plan) {
        const cur = tasks.find(t => t.taskId === id)
        if (!cur || !sameDay(cur.startDate, v.startDate) || !sameDay(cur.endDate, v.endDate)) changedPlan.set(id, v)
      }

      // 표시 기간 밖으로 나가는 날짜 — 기준 공종이면 '표시 기간을 벗어나요', 후행이면 '표시 기간을 벗어나는 후행이 있어요'
      let outsideAnchor = false
      let outsideOther = false
      for (const [id, v] of changedPlan) {
        if (v.startDate.getTime() < movableRange.start.getTime() || v.endDate.getTime() > movableRange.end.getTime()) {
          if (id === task.taskId) outsideAnchor = true; else outsideOther = true
        }
      }
      if (outsideAnchor || outsideOther) {
        return { ...none, error: outsideAnchor ? '표시 기간을 벗어나요' : '표시 기간을 벗어나는 후행이 있어요' }
      }

      const warnings = predecessorWarnings(changedPlan, tasks, deps, holidays).map(w => w.message)
      const hasOrderRow = [...plan.keys()].some(id => {
        const t = tasks.find(x => x.taskId === id)
        return !!t && (t.orderDate != null || t.deliveryDate != null)
      })
      return { plan, error: null, warnings, changed: changedPlan.size > 0, hasOrderRow }
    } catch (e) {
      return { ...none, error: e instanceof RangeError ? e.message : '날짜를 계산하지 못했어요' }
    }
  }, [holidayOk, holidayState, holidays, start, end, withSuccessors, hasSuccessors, tasks, deps, task.taskId, movableRange])

  const canApply = holidayOk && result.plan !== null && result.error === null && result.changed

  const apply = () => {
    if (!canApply || !result.plan) return
    if (onApply(result.plan, { syncOrderDates: result.hasOrderRow && syncOrders })) onClose()
  }

  return (
    <Popover open onClose={onClose} anchorRef={anchorRef} width={512} aria-label="공사 기간 수정" align="start">
      <div className="flex flex-col" style={{ gap: 'var(--num-12)' }}>
        <div style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>
          공사 기간 수정 · {task.workTypeName}
        </div>

        {holidayState.status === 'error' && (
          <Alert kind="error" action={<Button variant="secondary" size="sm" onClick={holidayState.retry}>다시 시도</Button>}>
            공휴일 정보를 불러오지 못했어요 · 다시 시도하기 전에는 기간을 바꿀 수 없어요
          </Alert>
        )}
        {holidayState.status !== 'error' && !holidayOk && (
          <Alert kind="info">공휴일 정보를 불러오는 중이에요</Alert>
        )}

        <div className="flex items-end" style={{ gap: 'var(--num-8)' }}>
          <DatePicker
            className="flex-1 min-w-0"
            label="시작일"
            value={isoOf(start)}
            minDate={minIso}
            maxDate={maxIso}
            holidays={holidays}
            disabled={!holidayOk}
            onChange={v => pickStart(fromIso(v))}
          />
          <DatePicker
            className="flex-1 min-w-0"
            label="종료일"
            value={isoOf(end)}
            minDate={isoOf(start)}
            maxDate={maxIso}
            holidays={holidays}
            disabled={!holidayOk}
            onChange={v => pickEnd(fromIso(v))}
          />
          <div className="field">
            <label>일수(작업일)</label>
            <Counter value={days} min={1} max={MAX_DAYS} onChange={changeDays} aria-label="소요일(작업일 수)" />
          </div>
        </div>

        <DateRangeCalendar
          value={{ start, end }}
          holidays={holidays}
          minDate={movableRange.start}
          maxDate={movableRange.end}
          onChange={v => {
            if (!holidayOk) return
            if (v.end === null) pickStart(v.start as Date)
            else pickEnd(v.end)
          }}
        />

        {hasSuccessors && (
          <Checkbox
            label="후행 공정도 같이 이동"
            checked={withSuccessors}
            onChange={e => setWithSuccessors(e.target.checked)}
          />
        )}
        {result.hasOrderRow && (
          <Checkbox
            label="발주일 · 입고일도 리드타임 기준으로 함께 이동해요"
            checked={syncOrders}
            onChange={e => setSyncOrders(e.target.checked)}
          />
        )}

        {result.error && <Alert kind="error">{result.error}</Alert>}
        {result.warnings.length > 0 && (
          <Alert kind="warning">
            {result.warnings.slice(0, 3).map(m => <div key={m}>{m}</div>)}
            {result.warnings.length > 3 && <div>외 {result.warnings.length - 3}건</div>}
          </Alert>
        )}

        <div className="flex items-center justify-end" style={{ gap: 'var(--num-8)' }}>
          <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
          <Button variant="primary" size="sm" disabled={!canApply} onClick={apply}>적용</Button>
        </div>
      </div>
    </Popover>
  )
}
